import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, deletePaymentConfig, getPaymentConfig, putPaymentConfig } from './api/client';
import type { PaymentConfigResponse } from './api/types';

// The platform's known payment-provider ids — convention, defined by the gateways the
// stripe-integration appliance composes (ADR-020). `fake` is composed in local/dev only.
const PROVIDERS: readonly string[] = ['fake', 'stripe', 'vipps', 'google-play', 'apple-iap'];

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

/** GET returns this code (404) when the tenant simply has no config yet — a normal empty state. */
function isNotConfigured(e: unknown): boolean {
  return e instanceof ApiError && e.body.code === 'TenantPaymentConfig.NotConfigured';
}

const row = { display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.35rem 0' } as const;

/**
 * Manage one tenant's payment configuration (view / create-or-replace / remove) — the vendor's
 * selection of which payment provider charges its buyers (ADR-020), consumed at checkout time by
 * the stripe-integration appliance. There is at most one config per tenant, so this is a single
 * form rather than a list. Provider credentials stay platform-level, so no secrets ride here;
 * writes are Owner-gated server-side (ADR-017) — this panel is a UX mirror.
 */
export function PaymentConfigPanel({ tenant }: { tenant: string }) {
  const [config, setConfig] = useState<PaymentConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // form fields
  const [provider, setProvider] = useState('fake');
  const [enabled, setEnabled] = useState(true);

  // Mirror a loaded config (or a cleared "not configured" state) into the form fields.
  function applyConfig(c: PaymentConfigResponse | null): void {
    setConfig(c);
    setProvider(c?.provider ?? 'fake');
    setEnabled(c ? c.enabled : true);
  }

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      applyConfig(await getPaymentConfig(tenant));
    } catch (e: unknown) {
      if (isNotConfigured(e)) {
        applyConfig(null);
      } else {
        setError(describeError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  // Reload whenever the acting-as tenant changes.
  useEffect(() => {
    void load();
  }, [tenant]);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await putPaymentConfig(tenant, { provider, enabled });
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onRemove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deletePaymentConfig(tenant);
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>
        Payment for &ldquo;{tenant}&rdquo;{' '}
        <button onClick={() => void load()} disabled={busy}>
          Reload
        </button>
      </h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      {config === null ? (
        <p>
          <em>No payment provider configured for &ldquo;{tenant}&rdquo;.</em>
        </p>
      ) : (
        <p>
          Configured — provider <strong>{config.provider}</strong>, {config.enabled ? 'enabled' : 'disabled'}.
        </p>
      )}

      <form onSubmit={(e) => void onSave(e)}>
        <div style={row}>
          <label>
            Provider{' '}
            <select value={provider} onChange={(e) => setProvider(e.currentTarget.value)}>
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={row}>
          <label>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
            />{' '}
            Enabled
          </label>
        </div>
        <div style={row}>
          <button type="submit" disabled={busy}>
            Save
          </button>
          {config !== null && (
            <button type="button" onClick={() => void onRemove()} disabled={busy}>
              Remove
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
